/**
 * Transparency scores.
 *
 * A WAI-ARIA tab set: six grade tiles select one description panel. Below 750px
 * the tab row is visually reduced to the selected tile and prev/next arrows plus
 * dots drive it instead, but every tab stays in the accessibility tree so the
 * tablist keeps working for keyboard and screen reader users.
 *
 * Progressive enhancement: the section server-renders every panel visible and
 * stacked. This element adds `ts--enhanced`, which is what collapses it to one
 * panel at a time. With JS off or broken the failure mode is "all six
 * descriptions readable", not "empty box".
 *
 * Liquid cannot read query strings, so the ?score= deep link is handled here.
 */
class TransparencyScores extends HTMLElement {
  #tabs = [];
  #panels = [];
  #dots = [];
  #selected = 0;

  connectedCallback() {
    this.#tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.#panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.#dots = Array.from(this.querySelectorAll('[data-ts-dot]'));

    if (this.#tabs.length === 0) return;

    this.classList.add('ts--enhanced');

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);

    // Order matters: #initialIndex() is what records a deep-link hit.
    const index = this.#initialIndex();
    const isDeepLink = this.dataset.deeplinkHit === 'pending';

    this.#select(index, { focus: false, scroll: isDeepLink });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
  }

  /**
   * The ?score= value wins over the default, matching a tab's label
   * case-insensitively. URLSearchParams already decodes, so `B%2B` arrives as
   * `B+`.
   *
   * @returns {number}
   */
  #initialIndex() {
    if (this.dataset.deeplink !== 'true') return 0;

    const param = this.dataset.scoreParam;
    if (!param) return 0;

    let wanted;
    try {
      wanted = new URLSearchParams(window.location.search).get(param);
    } catch {
      return 0;
    }
    if (!wanted) return 0;

    wanted = wanted.trim().toLowerCase();
    const found = this.#tabs.findIndex((tab) => (tab.dataset.score || '').trim().toLowerCase() === wanted);

    if (found === -1) return 0;

    this.dataset.deeplinkHit = 'pending';
    return found;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const tab = target.closest('[role="tab"]');
    if (tab instanceof HTMLElement) {
      this.#select(this.#tabs.indexOf(tab));
      return;
    }

    const dot = target.closest('[data-ts-dot]');
    if (dot instanceof HTMLElement) {
      this.#select(Number(dot.dataset.tsDot));
      return;
    }

    const arrow = target.closest('[data-ts-step]');
    if (arrow instanceof HTMLElement) {
      const step = Number(arrow.dataset.tsStep);
      this.#select(this.#wrap(this.#selected + step));
    }
  };

  /** Roving tabindex per the WAI-ARIA tabs pattern. @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.closest('[role="tab"]')) return;

    const moves = {
      ArrowRight: this.#selected + 1,
      ArrowLeft: this.#selected - 1,
      ArrowDown: this.#selected + 1,
      ArrowUp: this.#selected - 1,
      Home: 0,
      End: this.#tabs.length - 1,
    };

    if (!(event.key in moves)) return;

    event.preventDefault();
    this.#select(this.#wrap(moves[event.key]));
  };

  /** @param {number} index @returns {number} */
  #wrap(index) {
    const total = this.#tabs.length;
    return ((index % total) + total) % total;
  }

  /**
   * @param {number} index
   * @param {{focus?: boolean, scroll?: boolean}} [options]
   */
  #select(index, options = {}) {
    const { focus = true, scroll = false } = options;
    if (index < 0 || index >= this.#tabs.length) return;

    this.#selected = index;

    this.#tabs.forEach((tab, i) => {
      const isSelected = i === index;
      tab.setAttribute('aria-selected', String(isSelected));
      // Only the selected tab is reachable by Tab; arrows move within the set.
      tab.setAttribute('tabindex', isSelected ? '0' : '-1');
      tab.toggleAttribute('data-ts-current', isSelected);
    });

    this.#panels.forEach((panel, i) => {
      panel.toggleAttribute('hidden', i !== index);
    });

    this.#dots.forEach((dot, i) => {
      dot.toggleAttribute('data-ts-current', i === index);
    });

    if (focus) this.#tabs[index].focus();
    if (scroll) this.#scrollIntoView();
  }

  /** Only used for a deep link, where the visitor arrived aimed at this section. */
  #scrollIntoView() {
    delete this.dataset.deeplinkHit;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
  }
}

if (!customElements.get('transparency-scores')) {
  customElements.define('transparency-scores', TransparencyScores);
}
